import useThunkDispatch from 'common/hooks/useThunkDispatch';
import FetchStatus from 'common/types/FetchStatus';
import constants from 'domain/constants';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { attemptLogin } from '../AuthSlice';
import LoginPresenter from './LoginPresenter';

type LoginLocationState = {
  showVerifySuccess?: boolean;
};

function LoginContainer() {
  const { state } = useLocation<LoginLocationState>();
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fetchStatus, setFetchStatus] = useState(FetchStatus.none);
  const dispatch = useThunkDispatch();
  const [attempts, setAttempts] = useState(0);

  const login = useCallback(
    async (email: string, pw: string) => {
      try {
        setError('');
        setFetchStatus(FetchStatus.loading);
        await dispatch(attemptLogin(email, pw));
      } catch (err) {
        if (err === 'REQUIRE_EMAIL_VERIFICATION') {
          setConfirmEmail(true);
        } else if (
          err instanceof TypeError &&
          err.message === constants.FAILED_TO_FETCH
        ) {
          setError('Unable to connect to server.');
        } else if (typeof err === 'string') {
          setError(err);
        } else {
          setError('Unable to log in. Please try again later.');
        }
      } finally {
        setFetchStatus(FetchStatus.loaded);
      }
    },
    [dispatch]
  );

  const handleLoginClick = useCallback(async () => {
    setAttempts((prev) => prev + 1);
    if (username && password) {
      await login(username, password);
    }
  }, [login, username, password]);

  const handleDevLogin = () => {
    if (devEmail && devPassword) {
      login(devEmail, devPassword);
    }
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleLoginClick();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [handleLoginClick]);

  const devEmail = process.env.REACT_APP_DEV_EMAIL;
  const devPassword = process.env.REACT_APP_DEV_PASSWORD;

  return (
    <LoginPresenter
      username={username}
      password={password}
      error={error}
      confirmEmail={confirmEmail}
      fetchStatus={fetchStatus}
      onUsernameChange={(value) => {
        setUsername(value);
      }}
      onPasswordChange={(value) => {
        setPassword(value);
      }}
      onLoginClick={handleLoginClick}
      showEmailVerified={Boolean(state && state.showVerifySuccess)}
      hasDevCredentials={Boolean(devEmail) && Boolean(devPassword)}
      onDevLoginClick={handleDevLogin}
      hasSubmitted={attempts > 0}
    />
  );
}

export default LoginContainer;
